import { type FC, useEffect, useState } from 'react';
import { LinkType, LINK_TYPE_COLORS } from '@/types/link.types';
import { NodeType, NODE_TYPE_COLORS } from '@/types/node.types';

const nodeLabels: Record<NodeType, { label: string; icon: string }> = {
  [NodeType.Vision]: { label: 'Vision / Mission', icon: '★' },
  [NodeType.Objective]: { label: 'Objectif stratégique', icon: '◎' },
  [NodeType.Activity]: { label: 'Activité / Projet', icon: '⚡' },
  [NodeType.Risk]: { label: 'Risque', icon: '⚠' },
  [NodeType.Opportunity]: { label: 'Opportunité', icon: '✨' },
  [NodeType.Resource]: { label: 'Ressource (RH/matériel)', icon: '⚙' },
  [NodeType.KPI]: { label: 'KPI / Indicateur', icon: '📊' },
  [NodeType.Stakeholder]: { label: 'Partie prenante', icon: '\u{1F465}' },
};

const linkLabels: Record<LinkType, { label: string; style: string }> = {
  [LinkType.Hierarchy]: { label: 'Hiérarchie', style: 'solid' },
  [LinkType.Dependency]: { label: 'Dépendance', style: 'dashed' },
  [LinkType.Contribution]: { label: 'Contribution', style: 'solid' },
  [LinkType.Contradiction]: { label: 'Contradiction', style: 'solid' },
  [LinkType.Complementarity]: { label: 'Complémentarité', style: 'dashed' },
  [LinkType.FreeForm]: { label: 'Libre', style: 'dotted' },
};

export const Legend: FC = () => {
  // Collapsed by default on small screens (touch / mobile)
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setCollapsed((c) => (c ? c : true));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    userSelect: 'none',
  };

  return (
    <div
      className="canvas-legend"
      style={{
        position: 'absolute',
        top: 'var(--spacing-md)',
        right: 'var(--spacing-md)',
        zIndex: 10,
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
        padding: collapsed ? '4px 10px' : 'var(--spacing-sm) var(--spacing-md)',
        fontSize: 'var(--font-size-xs)',
        maxWidth: 200,
      }}
    >
      <div style={headerStyle} onClick={() => setCollapsed((c) => !c)} title={collapsed ? 'Afficher la légende' : 'Masquer la légende'}>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          {collapsed ? '▸' : '▾'}
        </span>
        <span
          style={{
            fontWeight: 600,
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Légende
        </span>
      </div>

      {!collapsed && (
        <>
          <div style={{ marginTop: 8, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>
            Noeuds
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 }}>
            {Object.entries(nodeLabels).map(([type, { label, icon }]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 3,
                    backgroundColor: NODE_TYPE_COLORS[type as NodeType],
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {icon}
                </span>
                <span style={{ color: 'var(--color-text)' }}>{label}</span>
              </div>
            ))}
          </div>

          <div style={{ fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>
            Liaisons
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Object.entries(linkLabels).map(([type, { label, style }]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="24" height="10">
                  <line
                    x1="0"
                    y1="5"
                    x2="24"
                    y2="5"
                    stroke={LINK_TYPE_COLORS[type as LinkType]}
                    strokeWidth={2}
                    strokeDasharray={
                      style === 'dashed' ? '4 3' : style === 'dotted' ? '2 2' : undefined
                    }
                  />
                </svg>
                <span style={{ color: 'var(--color-text)' }}>{label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Legend;
